import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import type { SearchDataType } from '../service/types';
import { SEARCH_DEFAULT_VALUES } from '../constants/searchDefaults';
import { logout } from './userSlice';

export type SearchTab = 'filter_search' | 'profile_id_search' | 'mobile_number_search';

export type SearchSection =
  | 'basic'
  | 'professional'
  | 'family'
  | 'horoscope'
  | 'homeLocation'
  | 'presentLocation';

export interface SearchState {
  draft: SearchDataType;
  applied: SearchDataType | null;
  view: 'form' | 'results';
  activeTab: SearchTab;
  openSections: Record<SearchSection, boolean>;
  resultsScrollY: number;
}

const initialState: SearchState = {
  draft: SEARCH_DEFAULT_VALUES,
  applied: null,
  view: 'form',
  activeTab: 'filter_search',
  openSections: {
    basic: false,
    professional: false,
    family: false,
    horoscope: false,
    homeLocation: false,
    presentLocation: false,
  },
  resultsScrollY: 0,
};

const searchSlice = createSlice({
  name: 'search',
  initialState,
  reducers: {
    saveDraft(state, action: PayloadAction<SearchDataType>) {
      state.draft = action.payload;
    },
    applySearch(state, action: PayloadAction<SearchDataType>) {
      state.draft = action.payload;
      state.applied = action.payload;
      state.view = 'results';
      state.resultsScrollY = 0;
    },
    backToForm(state) {
      state.view = 'form';
    },
    setActiveTab(state, action: PayloadAction<SearchTab>) {
      state.activeTab = action.payload;
    },
    toggleSection(state, action: PayloadAction<SearchSection>) {
      state.openSections[action.payload] = !state.openSections[action.payload];
    },
    setResultsScroll(state, action: PayloadAction<number>) {
      state.resultsScrollY = action.payload;
    },
    clearSearch(state) {
      state.draft = SEARCH_DEFAULT_VALUES;
      state.applied = null;
    },
  },
  extraReducers: (builder) => {
    // Never carry one user's search filters over to the next login
    builder.addCase(logout, () => initialState);
  },
});

export const {
  saveDraft,
  applySearch,
  backToForm,
  setActiveTab,
  toggleSection,
  setResultsScroll,
  clearSearch,
} = searchSlice.actions;

export default searchSlice.reducer;
